// v1.1 modules (Phase 12): AR / city ledger (02b §16), group blocks (02 §3.5), forecasting (02 §3.21).
import { z } from 'zod';
import { paymentMethodSchema } from './billing.js';
import { dateSchema, moneySchema, uuidSchema } from './common.js';
const amount4 = moneySchema.shape.amount;
const positive4 = amount4.refine((a) => !a.startsWith('-') && a !== '0.0000', 'must be > 0');
const iso4217 = z.string().regex(/^[A-Z]{3}$/);
// ---------------------------------------------------------------- AR
export const companyAccountInputSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9_-]{2,16}$/),
    nameAr: z.string().trim().min(2).max(160),
    nameEn: z.string().trim().max(160).optional(),
    taxId: z.string().trim().max(40).optional(),
    address: z.string().trim().max(300).optional(),
    contactName: z.string().trim().max(120).optional(),
    contactPhone: z.string().trim().max(40).optional(),
    contactEmail: z.string().trim().email().max(120).optional(),
    /** base currency, 4 dp (BR-AR-02) */
    creditLimitBase: amount4.default('0.0000'),
    paymentTermsDays: z.number().int().min(0).max(365).default(30),
    status: z.enum(['active', 'suspended', 'closed']).default('active'),
    notes: z.string().trim().max(1000).optional(),
});
export const transferToArSchema = z.object({
    companyAccountId: uuidSchema,
    /** BR-AR-02: needs ar.credit_limit.override + a reason */
    overrideCreditLimit: z.boolean().default(false),
    reason: z.string().trim().max(300).optional(),
});
export const arPaymentSchema = z.object({
    amount: positive4,
    currency: iso4217,
    method: paymentMethodSchema,
    reference: z.string().trim().max(80).optional(),
    /** manual allocation (base amounts); omitted → FIFO over open invoices (REQ-AR-03) */
    allocations: z
        .array(z.object({ invoiceId: uuidSchema, amountBase: positive4 }))
        .max(50)
        .optional(),
});
export const arAdjustmentSchema = z.object({
    /** signed, base currency */
    amountBase: amount4.refine((a) => a !== '0.0000', 'must not be zero'),
    reason: z.string().trim().min(3).max(300),
    invoiceId: uuidSchema.optional(),
});
// ---------------------------------------------------------------- group blocks
export const groupBlockInputSchema = z
    .object({
    name: z.string().trim().min(2).max(160),
    companyAccountId: uuidSchema.optional(),
    contactName: z.string().trim().max(120).optional(),
    contactPhone: z.string().trim().max(40).optional(),
    arrival: dateSchema,
    departure: dateSchema,
    cutoffDate: dateSchema,
    ratePlanId: uuidSchema,
    rooms: z
        .array(z.object({ roomTypeId: uuidSchema, blocked: z.number().int().min(0).max(500) }))
        .min(1)
        .max(20),
    notes: z.string().trim().max(1000).optional(),
    /** REQ-GRP-06: room nights of the pick-ups post to the block's master folio */
    routing: z.enum(['none', 'room_charges']).default('none'),
    /** BR-GRP-01: raising a block beyond availability needs reservation.overbook */
    allowOverbooking: z.boolean().default(false),
})
    .refine((v) => v.departure > v.arrival, 'departure must be after arrival')
    .refine((v) => v.cutoffDate <= v.arrival, 'cut-off must be on or before arrival');
export const roomingListSchema = z.object({
    entries: z
        .array(z.object({
        guestId: uuidSchema,
        roomTypeId: uuidSchema,
        adults: z.number().int().min(1).max(10).default(1),
        children: z.number().int().min(0).max(10).default(0),
        roomId: uuidSchema.optional(),
    }))
        .min(1)
        .max(200),
});
export const groupCancelSchema = z.object({ reason: z.string().trim().min(3).max(300) });
/** REQ-GRP-05 bulk check-in: the same switches as a single check-in, applied to every pick-up arriving today */
export const groupCheckInSchema = z.object({
    allowDirtyRoom: z.boolean().default(false),
    allowIdentityPending: z.boolean().default(true),
});
// ---------------------------------------------------------------- replay reconciliation (ADR-0021)
export const replayDismissSchema = z.object({ note: z.string().trim().min(2).max(300) });
// ---------------------------------------------------------------- forecast
export const forecastQuerySchema = z.object({
    from: dateSchema.optional(),
    days: z.coerce.number().int().min(1).max(90).default(30),
    /** BR-FCST-02: per-property parameters; query overrides the stored ones for what-if */
    tentativeWeight: z.coerce.number().min(0).max(1).optional(),
    weeks: z.coerce.number().int().min(1).max(26).optional(),
});
//# sourceMappingURL=v11.js.map